import { useState } from "react";
import axios from "axios";
import { Lock, Loader2, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  awaiting_payment: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
  paid: "border-sky-400/50 text-sky-300 bg-sky-400/10",
  shipped: "border-emerald-400/50 text-emerald-300 bg-emerald-400/10",
};
const STATUS_LABEL = { awaiting_payment: "Awaiting Payment", paid: "Paid", shipped: "Shipped" };

export default function AdminOrders() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const adminFetch = (path, options = {}) =>
    axios({ url: `${API}${path}`, headers: { "X-Admin-Passcode": passcode }, ...options });

  const loadOrders = async () => {
    const res = await adminFetch("/admin/orders");
    setOrders(res.data.orders);
  };

  const unlock = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setBusy(true);
    setError("");
    try {
      await loadOrders();
      setAuthed(true);
    } catch {
      setError("Wrong passcode.");
      setAuthed(false);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (orderNumber, status) => {
    try {
      const res = await adminFetch(`/admin/orders/${orderNumber}/status`, { method: "POST", data: { status } });
      toast.success(
        `${orderNumber} marked ${STATUS_LABEL[status]}${res.data.email_sent ? " — customer emailed" : ""}`
      );
      await loadOrders();
    } catch {
      toast.error("Could not update the order.");
    }
  };

  const lock = () => {
    setAuthed(false);
    setOrders([]);
    setPasscode("");
  };

  if (!authed) {
    return (
      <main data-testid="admin-login" className="min-h-screen flex items-center justify-center px-5">
        <Seo title="Owner Login — Always & Forever" siteName="Always & Forever" description="Store owner area." />
        <form onSubmit={unlock} className="glass-panel rounded-sm p-10 w-full max-w-sm text-center">
          <Lock className="w-8 h-8 mx-auto text-[#ff1053] mb-6" />
          <h1 className="font-display font-medium uppercase tracking-tight text-2xl mb-2">Owner Area</h1>
          <p className="text-xs text-white/45 font-light mb-8">Enter your passcode to manage orders.</p>
          <input
            data-testid="admin-passcode-input"
            type="password"
            autoComplete="current-password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            className="w-full bg-white/5 border border-white/15 rounded-sm px-4 py-3.5 text-sm text-center placeholder:text-white/25 focus:outline-none focus:border-[#ff1053]/70 transition-colors mb-4"
          />
          {error && <p data-testid="admin-error" className="text-xs text-[#ff1053] mb-4">{error}</p>}
          <button
            data-testid="admin-unlock-btn"
            type="submit"
            disabled={busy || !passcode}
            className="w-full bg-white text-black rounded-full py-3.5 text-xs uppercase tracking-[0.25em] font-semibold transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50"
          >
            {busy ? "Checking…" : "Unlock"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-orders" className="min-h-screen px-5 sm:px-10 py-24 max-w-5xl mx-auto">
      <Seo title="Orders — Always & Forever" siteName="Always & Forever" description="Store owner area." />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-12">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#ff1053] mb-2">Owner Area</p>
          <h1 className="font-display font-medium uppercase tracking-tight text-4xl">Orders</h1>
        </div>
        <div className="flex gap-3">
          <button data-testid="admin-refresh-btn" onClick={loadOrders} className="flex items-center gap-2 border border-white/25 rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button data-testid="admin-lock-btn" onClick={lock} className="flex items-center gap-2 border border-white/25 rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Lock
          </button>
        </div>
      </div>

      {orders.length === 0 ? (
        <p data-testid="admin-empty" className="text-white/40 text-sm font-light text-center mt-20">No orders yet.</p>
      ) : (
        <div className="flex flex-col gap-6">
          {orders.map((o) => (
            <article key={o.order_number} data-testid={`admin-order-${o.order_number}`} className="glass-panel rounded-sm p-6 sm:p-8">
              <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                <div>
                  <p className="font-unbounded text-lg">{o.order_number}</p>
                  <p className="text-[10px] text-white/40 mt-1">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[9px] uppercase tracking-[0.2em] border rounded-full px-4 py-1.5 ${STATUS_STYLE[o.status] || ""}`}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                  <span className="font-unbounded text-[#ff1053]">${o.total.toFixed(2)}</span>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-6 text-sm">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">Items</p>
                  <ul className="flex flex-col gap-1.5">
                    {o.items.map((it, i) => (
                      <li key={i} className="text-white/80">{it.name} <span className="text-white/40">· {it.size} × {it.quantity}</span></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">Ship To</p>
                  <p className="text-white/80 leading-relaxed">
                    {o.full_name}<br />
                    {o.address.line1}{o.address.line2 ? <><br />{o.address.line2}</> : null}<br />
                    {o.address.city}, {o.address.state} {o.address.zip}
                  </p>
                  <p className="text-white/45 text-xs mt-2">{o.email}{o.phone ? <> · {o.phone}</> : null}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">Payment</p>
                  <p className="text-white/80">{o.payment_method === "zelle" ? "Zelle" : "Cash App"}</p>
                  <p className="text-white/45 text-xs mt-1">Sender: {o.sender_name}</p>
                  <div className="flex flex-wrap gap-2 mt-5">
                    {o.status === "awaiting_payment" && (
                      <button
                        data-testid={`mark-paid-${o.order_number}`}
                        onClick={() => setStatus(o.order_number, "paid")}
                        className="bg-white text-black rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.18em] font-semibold hover:scale-105 transition-transform"
                      >
                        Mark Paid
                      </button>
                    )}
                    {o.status === "paid" && (
                      <button
                        data-testid={`mark-shipped-${o.order_number}`}
                        onClick={() => setStatus(o.order_number, "shipped")}
                        className="bg-[#ff1053] text-white rounded-full px-5 py-2.5 text-[10px] uppercase tracking-[0.18em] font-semibold hover:scale-105 transition-transform"
                      >
                        Mark Shipped
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
